// src/admin/AdminReturns.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { RefreshCw, ChevronDown, ChevronUp, CheckCircle, XCircle, Package } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  pending:          'bg-amber-100 text-amber-700',
  approved:         'bg-green-100 text-green-700',
  rejected:         'bg-red-100 text-red-700',
  refunded:         'bg-blue-100 text-blue-700',
  pickup_scheduled: 'bg-purple-100 text-purple-700',
};

function ReturnCard({ ret, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [note,     setNote]     = useState(ret.adminNote || '');
  const [refund,   setRefund]   = useState(ret.refundAmount || ret.orderTotal || 0);
  const date = ret.submittedAt?.toDate?.()?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) || '—';

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      const updates = {
        status:     newStatus,
        adminNote:  note.trim(),
        reviewedAt: serverTimestamp(),
      };
      if (newStatus === 'refunded') updates.refundAmount = Number(refund);
      await updateDoc(doc(db, 'returns', ret.id), updates);
      onUpdate(ret.id, { ...updates, status: newStatus });
      toast.success(`Return marked as ${newStatus}`);
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <p className="font-body font-bold text-gray-900 text-sm">
            Return #{ret.id.slice(-6).toUpperCase()} · Order #{ret.orderId?.slice(-8).toUpperCase()}
          </p>
          <p className="text-gray-400 font-body text-xs">{ret.userName} · {ret.userEmail} · {date}</p>
          <p className="text-gray-600 font-body text-xs mt-0.5"><strong>Reason:</strong> {ret.reason}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <p className="font-display font-bold text-gray-900 text-sm">₹{ret.orderTotal?.toLocaleString('en-IN')}</p>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full capitalize ${STATUS_COLORS[ret.status] || STATUS_COLORS.pending}`}>
            {ret.status || 'pending'}
          </span>
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-5 space-y-4">
          {/* Items */}
          <div className="space-y-2">
            {ret.items?.map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                <img src={item.image} alt={item.name} className="w-12 h-12 rounded-xl object-cover shrink-0" />
                <div>
                  <p className="font-body font-medium text-gray-900 text-sm">{item.name}</p>
                  <p className="text-gray-400 text-xs font-body">Qty: {item.quantity}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Customer description */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="font-body font-semibold text-amber-700 text-xs mb-1">Customer's Description</p>
            <p className="font-body text-gray-700 text-sm">{ret.description}</p>
          </div>

          {/* Admin controls */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Admin Note (shown to customer)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Explain the decision to the customer..."
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-sm resize-none" />
            </div>
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Refund Amount (₹)</label>
              <input type="number" value={refund} onChange={(e) => setRefund(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-sm" />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => updateStatus('approved')} disabled={saving || ret.status === 'approved'}
              className="flex items-center gap-1.5 bg-green-50 hover:bg-green-500 hover:text-white text-green-700 border border-green-200 px-4 py-2.5 rounded-xl font-body text-sm font-semibold transition-all disabled:opacity-50">
              <CheckCircle size={15} /> Approve Return
            </button>
            <button onClick={() => updateStatus('pickup_scheduled')} disabled={saving}
              className="flex items-center gap-1.5 bg-purple-50 hover:bg-purple-500 hover:text-white text-purple-700 border border-purple-200 px-4 py-2.5 rounded-xl font-body text-sm font-semibold transition-all disabled:opacity-50">
              <Package size={15} /> Schedule Pickup
            </button>
            <button onClick={() => updateStatus('refunded')} disabled={saving}
              className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-500 hover:text-white text-blue-700 border border-blue-200 px-4 py-2.5 rounded-xl font-body text-sm font-semibold transition-all disabled:opacity-50">
              <CheckCircle size={15} /> Mark Refunded
            </button>
            <button onClick={() => updateStatus('rejected')} disabled={saving || ret.status === 'rejected'}
              className="flex items-center gap-1.5 bg-red-50 hover:bg-red-500 hover:text-white text-red-600 border border-red-200 px-4 py-2.5 rounded-xl font-body text-sm font-semibold transition-all disabled:opacity-50">
              <XCircle size={15} /> Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminReturns() {
  const [returns,  setReturns]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('pending');

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, 'returns'));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.submittedAt?.toDate?.() || 0) - (a.submittedAt?.toDate?.() || 0));
        setReturns(list);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const onUpdate = (id, updates) =>
    setReturns((prev) => prev.map((r) => r.id === id ? { ...r, ...updates } : r));

  const FILTERS = ['all', 'pending', 'approved', 'pickup_scheduled', 'refunded', 'rejected'];
  const filtered = returns.filter((r) => filter === 'all' || r.status === filter);
  const counts   = returns.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display font-bold text-xl text-gray-900">Returns & Refunds</h2>
        <p className="text-gray-400 font-body text-sm">{returns.length} total · {counts.pending || 0} pending review</p>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-body font-medium capitalize transition-all ${
              filter === f ? 'bg-rose-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-rose-300'
            }`}>
            {f === 'all' ? 'All' : f.replace('_', ' ')}
            {f !== 'all' && counts[f] ? (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${filter === f ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {counts[f]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center h-40 items-center">
          <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <RefreshCw size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 font-body">No returns found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((ret) => <ReturnCard key={ret.id} ret={ret} onUpdate={onUpdate} />)}
        </div>
      )}
    </div>
  );
}
